import { useStaticRouteTitle } from "util/routeHelpers";
import { useEffect, useRef } from "react";

import { useLocation } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { HiddenText } from "foundations/HiddenText/HiddenText";

/**
 * A client-side route change doesn't trigger a real browser navigation, so screen readers never
 * get their usual "page loaded" cue. Keyboard focus just stays wherever it was (often a nav link,
 * whose `aria-current`/href now describe the page just landed on, so NVDA reads that stale state
 * (ex "current page") instead of anything about the new page. Most commonly when in a layout
 * with shared nav stuff.
 *
 * This moves focus to the new page's #main landmark and announces its title on every navigation
 * after the first, fixing both problems at once.
 */
export function RouteAnnouncer() {
  const intl = useIntl();
  const location = useLocation();
  const title = useStaticRouteTitle();
  const hasNavigated = useRef(false);

  // only focus main if we've navigated form elsewhere in the app, not on first load
  useEffect(() => {
    if (!hasNavigated.current) {
      hasNavigated.current = true;
      return;
    }
    document.getElementById("main")?.focus({ preventScroll: true });
  }, [location.pathname]);

  const announcement =
    hasNavigated.current && title
      ? intl.formatMessage(
          {
            defaultMessage: "Navigated to {title}",
            description:
              "Screen-reader announcement made after navigating to a new page",
          },
          { title },
        )
      : "";

  return (
    <HiddenText>
      <span role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>
    </HiddenText>
  );
}
